import { apiClient, API_BASE_URL } from "../index";
import { deepFixMojibake } from "../../utils/fixMojibake";

const VPN_CERTIFICATE_PATH = "/api/v1/cyber-range/vpn/certificate";
const SCENARIO_PATH = "/api/v1/cyber-range/scenario";
const ATTACKBOX_PATH = "/api/v1/cyber-range/attackbox";
const PROVISIONING_PATH = "/api/v1/cyber-range/provisioning";
const FLOW_RUN_PATH = "/api/v1/flow-run";
const TEAM_SCENARIO_PATH = "/api/v1/team-scenario";
const SCENARIO_ATTEMPTS_PATH = "/api/v1/scenario-attempts";
const MULTIVM_CYBER_RANGE_PATH = "/api/v1/multivm-cyberrange";

export async function requestVpnCertificate({ protocol, username }) {
  return apiClient.post(VPN_CERTIFICATE_PATH, {
    protocol,
    username,
  });
}

export async function provisionScenario({ scenarioId, username }) {
  return apiClient.post(SCENARIO_PATH, {
    scenario_id: scenarioId,
    username,
  });
}

export async function deleteScenario({ username, scenarioId }) {
  return apiClient.del(`${SCENARIO_PATH}/${username}/${scenarioId}?force=true`);
}

export async function listUserScenarios({ username }) {
  return apiClient.get(`${SCENARIO_PATH}/list/${username}`);
}

export async function provisionAttackBox({
  autoTerminateHours,
  instanceType,
  username,
  scenarioId,
}) {
  return apiClient.post(ATTACKBOX_PATH, {
    auto_terminate_hours: autoTerminateHours,
    instance_type: instanceType,
    username,
    scenario_id: scenarioId || undefined,
  });
}

export async function deleteAttackBox({ username }) {
  return apiClient.del(`${ATTACKBOX_PATH}/${username}?force=true`);
}

export async function getProvisioningStatus({ requestId }) {
  return apiClient.get(`${PROVISIONING_PATH}/${requestId}`);
}

export async function getLatestProvisioningStatus({
  resourceType,
  username,
  resourceKey,
}) {
  const query = resourceKey
    ? `?resource_key=${encodeURIComponent(resourceKey)}`
    : "";
  return apiClient.get(
    `${PROVISIONING_PATH}/latest/${resourceType}/${username}${query}`,
  );
}

const CATALOG_SCENARIO_PATH = "/api/v1/catalog/scenario";

export async function getScenarioDetails(scenarioId) {
  const data = await apiClient.get(`${CATALOG_SCENARIO_PATH}/${scenarioId}`);
  return deepFixMojibake(data);
}

export async function getScenarioTopologyRelevance(scenarioId) {
  return apiClient.get(
    `${CATALOG_SCENARIO_PATH}/${scenarioId}/topology/relevance`,
  );
}
// ─── Flow Run endpoints ───────────────────────────────────────────────────────

/**
 * Description mode: create a scenario from a vulnerability theme.
 */
export async function createScenarioThemeRun({
  cve,
  vuln_theme,
  category_id,
  team_id,
}) {
  return apiClient.post(`${FLOW_RUN_PATH}/create-scenario-theme-run`, {
    cve,
    vuln_theme,
    category_id,
    team_id,
  });
}

/**
 * Topology mode: create a scenario from a saved topology JSON.
 */
export async function createScenarioTopologyRun({
  cve,
  topology_id,
  category_id,
  team_id,
}) {
  return apiClient.post(`${FLOW_RUN_PATH}/create-scenario-topology-run`, {
    cve,
    topology_id,
    category_id,
    team_id,
  });
}

export async function startTeamScenario({ scenarioId, teamId }) {
  return apiClient.post(`${TEAM_SCENARIO_PATH}/start`, {
    scenario_id: scenarioId,
    team_id: teamId,
  });
}

// export async function stopTeamScenario({ scenarioId }) {
//   return apiClient.del(`${TEAM_SCENARIO_PATH}/${scenarioId}`);
// }
export async function stopTeamScenario({ scenarioId, teamId }) {
  const query = teamId
    ? `?${new URLSearchParams({ team_id: teamId }).toString()}`
    : "";
  return apiClient.del(`${TEAM_SCENARIO_PATH}/${scenarioId}${query}`);
}

export async function getStartedTeamScenarios({ teamId } = {}) {
  const query = teamId
    ? `?${new URLSearchParams({ team_id: teamId }).toString()}`
    : "";
  const data = await apiClient.get(`${TEAM_SCENARIO_PATH}/started${query}`);
  return deepFixMojibake(data);
}

export const getTeamScenario = async (scenarioId, teamId) => {
  const query = teamId
    ? `?${new URLSearchParams({ team_id: teamId }).toString()}`
    : "";
  const data = await apiClient.get(`${TEAM_SCENARIO_PATH}/${scenarioId}${query}`);
  return deepFixMojibake(data);
};

export const submitTeamAnswer = ({ scenarioId, questionIndex, answerText }) =>
  apiClient.post(`${TEAM_SCENARIO_PATH}/${scenarioId}/answers`, {
    question_index: questionIndex,
    answer_text: answerText,
  });

export const submitScenarioAnswer = ({
  scenarioId,
  questionIndex,
  answerText,
}) =>
  apiClient.post(`${SCENARIO_ATTEMPTS_PATH}/${scenarioId}/answers`, {
    question_index: questionIndex,
    answer_text: answerText,
  });

export const getScenarioAttemptStatus = (scenarioId) =>
  apiClient.get(`${SCENARIO_ATTEMPTS_PATH}/${scenarioId}/status`);

export async function getOrgStartedTeamScenarios() {
  const data = await apiClient.get(`${TEAM_SCENARIO_PATH}/org-started`);
  return deepFixMojibake(data);
}

export function getTeamAnswersStreamUrl({ scenarioId, teamId }) {
  const query = teamId
    ? `?${new URLSearchParams({ team_id: teamId }).toString()}`
    : "";
  return `${API_BASE_URL}${TEAM_SCENARIO_PATH}/${scenarioId}/answers/stream${query}`;
}

// ─── Multi-VM Lab (OVH Proxmox) endpoints ────────────────────────────────────

export async function provisionVmLab({ scenarioId, username }) {
  return apiClient.post(`${MULTIVM_CYBER_RANGE_PATH}/provision`, {
    scenario_id: scenarioId,
    username,
  });
}

export async function getVmLabProvisioningStatus({ requestId }) {
  return apiClient.get(`${MULTIVM_CYBER_RANGE_PATH}/provision/${requestId}`);
}

export async function getLatestVmLabStatus({ username }) {
  return apiClient.get(`${MULTIVM_CYBER_RANGE_PATH}/provision/latest/${username}`);
}

export async function getActiveVmLabs() {
  return apiClient.get(`${MULTIVM_CYBER_RANGE_PATH}/active`);
}

export async function getVmLabAccess({ slotId }) {
  return apiClient.get(`${MULTIVM_CYBER_RANGE_PATH}/active/${slotId}/access`);
}

export async function refreshVmLabUrl({ slotId }) {
  return apiClient.post(`${MULTIVM_CYBER_RANGE_PATH}/active/${slotId}/refresh-url`);
}

export async function stopVmLab({ slotId }) {
  return apiClient.del(`${MULTIVM_CYBER_RANGE_PATH}/active/${slotId}`);
}

export async function createMultiVmLab({ cve, vulnTheme }) {
  return apiClient.post(`${FLOW_RUN_PATH}/create-multivm-lab`, {
    cve,
    vuln_theme: vulnTheme,
    dummy: false,
  });
}
